<script setup>
import {computed} from 'vue';
import {useRouter} from 'vue-router';
import {useAuthService} from "~/composables/useAuthService.js";
import AvatarImageLoader from "@/components/loaders/AvatarImageLoader.vue";
import {challengeSchedule, toMs} from "~/utils/challengeSchedule";

// Ladder challenge row — ports the Flutter _ChallengeRow used on the ladder
// challenges screen (mockup ladder-challenges.html): a face, a natural-language
// title + subtitle, and either a "Respond" CTA (incoming + pending) or a status
// chip on the right.
const props = defineProps({
  challenge: {
    type: Object,
    required: true,
  },
  // kept for API compatibility with existing call sites
  actions: {
    type: Array,
    default: () => [],
  },
  myTeam: {
    type: Object,
    default: null,
  },
  // hairline border — off by default (the ladder list nests these inside an
  // already-bordered card with dividers). Opt in for standalone (timeline).
  bordered: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const {user} = useAuthService();
const {outputDateFromDb} = useCrudConverters();

const challengingTeam = computed(() => props.challenge?.challengingTeam);
const challengedTeam = computed(() => props.challenge?.challengedTeam);
const fixture = computed(() => props.challenge?.fixture || null);

const fmt = (d) => (d ? outputDateFromDb(d, 'EEE d MMMM') : '');

// Shared slim-model naming: singles/doubles teams have no Team.Name — the
// display name derives from the players ("TBC" only when there are none).
const {teamName: slimTeamName} = useTimelineModels();
// The API's side display name (ladder entry name, else team; doubles "A + B")
// for the challenge's own two sides, else the slim-model name.
const teamName = (team) => {
  const c = props.challenge;
  if (team && c && team.id === (c.challengingTeamId ?? c.challengingTeam?.id)) return c.challengingTeamDisplayName || slimTeamName(team);
  if (team && c && team.id === (c.challengedTeamId ?? c.challengedTeam?.id)) return c.challengedTeamDisplayName || slimTeamName(team);
  return slimTeamName(team);
};

// "my team" detection — prefer the passed myTeam, else user membership
const teamHasMe = (team) => {
  if (!team) return false;
  if (props.myTeam && team.id === props.myTeam.id) return true;
  const uid = user.value?.id;
  return !!uid && (team.players || []).some((p) => p.userId === uid);
};
const mineOutgoing = computed(() => teamHasMe(challengingTeam.value));
const mineIncoming = computed(() => teamHasMe(challengedTeam.value));

// --- status machine (ported from LadderChallenge) ---
const challengeStatus = computed(() => {
  const c = props.challenge;
  if (c.deletedAt) return 'cancelled';
  if (c.rejectedAt) return 'rejected';
  if (c.acceptedAt) return 'accepted';
  if (c.expiresAt && toMs(c.expiresAt) < Date.now()) return 'expired';
  if (fixture.value || c.fixtureId) return 'skipped';
  return 'made';
});
const played = computed(() => !!(fixture.value && fixture.value.homeTeamApprovedAt && fixture.value.awayTeamApprovedAt));
const pending = computed(() => challengeStatus.value === 'made');

// The time's state in the same words as the challenge page's When & where card
// (utils/challengeSchedule.js): Suggested / Proposed / Confirmed / Not scheduled.
const schedule = computed(() => challengeSchedule(props.challenge, fixture.value,
    {isChallenger: mineOutgoing.value, isChallenged: mineIncoming.value, isAdmin: false}));
const fmtTime = (d) => (d ? outputDateFromDb(d, 'EEE d MMM, HH:mm') : '');
// Who has to confirm an open proposal: "you" when it's the viewer's side.
const confirmerName = computed(() => {
  const side = schedule.value.proposer === 'challenger' ? 'challenged' : 'challenger';
  const mine = side === 'challenger' ? mineOutgoing.value : mineIncoming.value;
  if (mine) return 'you';
  return teamName(side === 'challenger' ? challengingTeam.value : challengedTeam.value);
});
const proposerName = computed(() => {
  const side = schedule.value.proposer;
  if (!side) return '';
  const mine = side === 'challenger' ? mineOutgoing.value : mineIncoming.value;
  if (mine) return 'You';
  return teamName(side === 'challenger' ? challengingTeam.value : challengedTeam.value);
});

// challenger-first result, e.g. "3 - 1"
const resultText = computed(() => {
  const f = fixture.value;
  if (!f) return null;
  const challengerIsHome = props.challenge.challengingTeamId === f.homeTeamId;
  return challengerIsHome
      ? `${f.homeTeamResult} - ${f.awayTeamResult}`
      : `${f.awayTeamResult} - ${f.homeTeamResult}`;
});

const winnerName = computed(() => {
  const f = fixture.value;
  if (!f) return '';
  const homePts = f.homeTeamPoints ?? 0;
  const awayPts = f.awayTeamPoints ?? 0;
  const challengerIsHome = props.challenge.challengingTeamId === f.homeTeamId;
  const challengerWon = challengerIsHome ? homePts > awayPts : awayPts > homePts;
  const team = challengerWon ? challengingTeam.value : challengedTeam.value;
  return teamHasMe(team) ? 'you' : teamName(team);
});

const title = computed(() => {
  const challenger = mineOutgoing.value ? 'you' : teamName(challengingTeam.value);
  const challenged = mineIncoming.value ? 'you' : teamName(challengedTeam.value);
  if (mineOutgoing.value) return `You → ${challenged}`;
  if (mineIncoming.value) return `${challenger} → you`;
  return props.challenge.acceptedAt ? `${challenger} vs ${challenged}` : `${challenger} → ${challenged}`;
});

const subtitle = computed(() => {
  const c = props.challenge;
  const sc = schedule.value;
  if (played.value) {
    return `Played${resultText.value ? ` · ${resultText.value} to ${winnerName.value}` : ''}`;
  }
  switch (sc.state) {
    case 'S7': return 'Result entered · awaiting approval';
    case 'S5':
    case 'S6': return `Confirmed · ${fmtTime(sc.when)}`;
    case 'S4': return `${proposerName.value} suggested ${fmtTime(sc.when)} · waiting for ${confirmerName.value}`;
    case 'S4p': return `${proposerName.value} suggested ${fmtTime(sc.when)} · time passed`;
    case 'S3': return 'Accepted · no time agreed yet';
  }
  // pending: the suggestion rides along, it is what they are being asked
  const suggestion = sc.state === 'S1' ? ` · suggested ${fmtTime(sc.when)}`
      : sc.state === 'S1p' ? (mineOutgoing.value ? ' · suggested time passed, suggest a new one' : ' · suggested time passed') : '';
  // told the challenger changed it since sending
  const changed = sc.changedAt ? ' · time changed' : '';
  switch (challengeStatus.value) {
    case 'made': return mineOutgoing.value
        ? `Sent ${fmt(c.createdAt)} · awaiting reply${suggestion}`
        : `Pending acceptance · expires ${fmt(c.expiresAt)}${suggestion}${changed}`;
    case 'expired': return `Expired ${fmt(c.expiresAt)}`;
    case 'rejected': return 'Declined';
    case 'cancelled': return 'Cancelled';
    default: return `Sent ${fmt(c.createdAt)}`;
  }
});

// right-side status chip — [label, kind, icon]
const chip = computed(() => {
  if (played.value) return {label: 'Played', kind: 'ok', icon: 'mdi-check'};
  const sc = schedule.value;
  switch (sc.state) {
    case 'S7': return {label: 'Result entered', kind: 'warn', icon: 'mdi-clock-outline'};
    // pending with a suggestion: the card's own chip (Suggested / Time passed)
    case 'S1':
    case 'S1p':
    case 'S3':
    case 'S4':
    case 'S4p':
    case 'S5':
    case 'S6': return {
      label: sc.chip.label,
      kind: sc.chip.kind === 'muted' ? 'neutral' : sc.chip.kind,
      icon: sc.chip.kind === 'ok' ? 'mdi-calendar-check' : sc.chip.kind === 'warn' ? 'mdi-clock-outline' : null,
    };
  }
  switch (challengeStatus.value) {
    case 'made': return {label: 'Pending', kind: 'warn', icon: 'mdi-clock-outline'};
    case 'expired': return {label: 'Expired', kind: 'neutral', icon: null};
    case 'rejected': return {label: 'Declined', kind: 'neutral', icon: null};
    case 'cancelled': return {label: 'Cancelled', kind: 'neutral', icon: null};
    default: return null;
  }
});

const showRespond = computed(() => mineIncoming.value && pending.value);

// face: the most relevant team (the challenger, unless you sent it)
const faceTeam = computed(() => (mineOutgoing.value ? challengedTeam.value : challengingTeam.value));
const faceImage = computed(() => faceTeam.value?.imageFileName || faceTeam.value?.players?.[0]?.imageFileName || null);
const faceInitials = computed(() => faceTeam.value?.initials || faceTeam.value?.name?.[0]?.toUpperCase() || '?');

const navigate = () => {
  if (!props.challenge?.id || !props.challenge?.ladderId) return;
  router.push({
    name: 'LadderChallengeDetail',
    params: {ladderId: props.challenge.ladderId, ladderChallengeId: props.challenge.id},
  });
};
</script>

<template>
  <div class="lc-row" :class="{ 'lc-row--bordered': bordered }" @click="navigate">
    <avatar-image-loader
        v-if="faceImage"
        :image-file-name="faceImage"
        :image-clickable="false"
        size="38"
        class="lc-face"
    />
    <span v-else class="lc-face lc-face--initials">{{ faceInitials }}</span>

    <div class="lc-who">
      <div class="lc-title">{{ title }}</div>
      <div class="lc-sub">{{ subtitle }}</div>
    </div>

    <button v-if="showRespond" type="button" class="lc-respond" @click.stop="navigate">Respond</button>
    <span v-else-if="chip" class="tl-chip" :class="`tl-chip--${chip.kind}`">
      <v-icon v-if="chip.icon" size="13" class="mr-1">{{ chip.icon }}</v-icon>
      {{ chip.label }}
    </span>
  </div>
</template>

<style scoped lang="scss">
.lc-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  background: rgb(var(--v-theme-surface));
}

.lc-row--bordered {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
}

.lc-face {
  width: 38px;
  height: 38px;
  border-radius: 50% !important;
  flex-shrink: 0;
}

.lc-face--initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-bg, 250, 250, 250));
  border: 1px solid rgb(var(--v-theme-borderLight));
  color: rgb(var(--v-theme-textSubtle));
  font-size: 12px;
  font-weight: 700;
}

.lc-who {
  flex: 1;
  min-width: 0;
}

.lc-title {
  font-size: 12.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lc-sub {
  font-size: 10.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* incoming + pending → brand "Respond" CTA */
.lc-respond {
  flex-shrink: 0;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgb(var(--v-theme-primary));
  color: #fff;
  font-size: 11.5px;
  font-weight: 600;
  border: none;
  cursor: pointer;

  &:hover {
    background: rgb(var(--v-theme-brandDark, var(--v-theme-primary)));
  }
}

/* status chip — layout only; colour vocabulary comes from the global .tl-chip */
.tl-chip {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
}
</style>
